import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { DEFAULT_VARIANT, IStateSelectorProps, useColourWithStatesCss, Variant } from '../../fundamentals/colours/variants';
import { OFFGRID_SIZES, PX_TO_REM, PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { ITextSize, Span } from '../Texts/Texts';
import { FONT_SIZES, FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { IGutterAsMarginProps, IGutterProps } from '../../fundamentals/spaces/space';
import { Icon, IconSize } from '../Icon/Icon';
import { MATERIAL_ICONS, MaterialIcon } from '../Icon/icons';
import { ButtonResetCssProps, useButtonOpacityStateCss, useButtonResetCss } from '../Button/ButtonRoot';
import { IStartIconBaseProps } from '../../common/interfaces';
import { BaseFocusCssProps, IActionCssProps, IForceFocusOutline, IInvalidProps, useActionCss, useFocusCss } from '../../fundamentals/actions';
import { ButtonTabBadgeRadiusSize } from '../../fundamentals/sizes/borderRadius';

export type BadgeSize = typeof SIZES.MICRO | typeof SIZES.XXX_SMALL | typeof SIZES.XX_SMALL | typeof SIZES.X_SMALL | typeof SIZES.SMALL;

export interface IBadgeProps
  extends Omit<IStateSelectorProps, 'variant'>,
    IGutterAsMarginProps,
    IStartIconBaseProps,
    Pick<React.HTMLAttributes<HTMLSpanElement>, 'tabIndex'>,
    React.Attributes {
  size?: BadgeSize;
  label?: string | React.ReactElement;
  deleteIcon?: MaterialIcon;
  onClose?: React.MouseEventHandler;
  prefix?: string;

  // IStateSelectorProps overrides
  variant?: Variant;
}

export const badgeTextSizes: Record<BadgeSize, ITextSize> = {
  [SIZES.MICRO]: FONT_SIZES.TEXT_NANO,
  [SIZES.XXX_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.XX_SMALL]: FONT_SIZES.TEXT_MICRO,
  [SIZES.X_SMALL]: FONT_SIZES.TEXT_SMALL,
  [SIZES.SMALL]: FONT_SIZES.TEXT_DEFAULT
};

export const badgeIconSizes: Record<BadgeSize, IconSize> = {
  [SIZES.MICRO]: SIZES.X_MICRO,
  [SIZES.XXX_SMALL]: SIZES.X_MICRO,
  [SIZES.XX_SMALL]: SIZES.X_MICRO,
  [SIZES.X_SMALL]: SIZES.MICRO,
  [SIZES.SMALL]: SIZES.XXX_SMALL
};

export const badgePaddingSizes = {
  [SIZES.MICRO]: `0 ${PX_TO_REM[8]}`,
  [SIZES.XXX_SMALL]: `0 ${PX_TO_REM[8]}`,
  [SIZES.XX_SMALL]: `0 ${PX_TO_REM[8]}`,
  [SIZES.X_SMALL]: `${PX_TO_REM[2]} ${PX_TO_REM[10]}`,
  [SIZES.SMALL]: `${PX_TO_REM[4]} ${PX_TO_REM[10]}`
};

export const badgeGaps: Record<BadgeSize, PxToRem> = {
  [SIZES.MICRO]: PX_TO_REM[4],
  [SIZES.XXX_SMALL]: PX_TO_REM[4],
  [SIZES.XX_SMALL]: PX_TO_REM[4],
  [SIZES.X_SMALL]: PX_TO_REM[6],
  [SIZES.SMALL]: PX_TO_REM[6]
};

export const borderRadii: Record<BadgeSize, ButtonTabBadgeRadiusSize> = {
  [SIZES.MICRO]: SIZES.XX_SMALL,
  [SIZES.XXX_SMALL]: OFFGRID_SIZES.OFFGRID_9,
  [SIZES.XX_SMALL]: OFFGRID_SIZES.OFFGRID_10,
  [SIZES.X_SMALL]: SIZES.X_SMALL,
  [SIZES.SMALL]: SIZES.SMALL
};

export type BadgeBaseCssProps = IBadgeProps & ButtonResetCssProps & BaseFocusCssProps & IActionCssProps;
export const useBadgeBaseCss = css<BadgeBaseCssProps>`
  ${useButtonResetCss};

  padding: ${({ size }) => badgePaddingSizes[size!]};

  column-gap: ${({ size }) => badgeGaps[size!]};

  height: ${({ size, theme }) => theme.sizes[size!]};

  font-size: ${({ size, theme }) => theme.fonts.fontSizes[badgeTextSizes[size!]]};
  line-height: ${({ size, theme }) => theme.fonts.lineHeightSizes[badgeTextSizes[size!]]};

  border-radius: ${({ theme, size }) => theme.buttonTabBadgeBorderRadius[borderRadii[size!]]};

  ${useFocusCss()}
  ${useActionCss}
`;

export const BadgeCloseIcon = styled(Icon)`
  ${useButtonOpacityStateCss};
`;

const BadgeContainer = styled(Span)<
  Required<Pick<IBadgeProps, 'variant'>> &
    Pick<IBadgeProps, 'light' | 'cssState' | 'size'> &
    IBadgeProps &
    IGutterProps &
    IInvalidProps &
    IForceFocusOutline &
    IActionCssProps &
    IStateSelectorProps
>`
  ${useBadgeBaseCss};

  ${useColourWithStatesCss};

  ${({ onClick }) =>
    onClick &&
    css`
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    `}
`;

export const Badge: React.FC<IBadgeProps> = styled(
  forwardRef<HTMLSpanElement, IBadgeProps>(
    (
      {
        label,
        startIcon,
        startIconType,
        deleteIcon,
        onClose,
        onClick,
        cssState,
        light,
        prefix = '',
        size = SIZES.X_SMALL,
        variant = DEFAULT_VARIANT,
        tabIndex,
        key,
        ...rest
      },
      ref
    ) => {
      const { generateTestId } = useTestId(rest, 'Badge');

      const handleOnClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose!(e);
      };

      const handleOnclick = (e: React.MouseEvent<HTMLSpanElement>) => {
        onClick?.(e);
        e.currentTarget.blur();
      };

      return (
        <BadgeContainer
          key={key}
          cssState={cssState}
          ref={ref}
          variant={variant}
          light={light}
          size={size}
          fontWeightCss={FONT_WEIGHTS.MEDIUM}
          onClick={onClick && handleOnclick}
          tabIndex={tabIndex ?? (onClick ? 0 : undefined)}
          noTextDecoration
          {...generateTestId(variant)}
          {...rest}
        >
          {prefix}
          {!!startIcon && <Icon type={startIconType} name={startIcon} size={badgeIconSizes[size]} {...generateTestId('Icon')} />}
          {label}
          {!!onClose && (
            <BadgeCloseIcon
              name={deleteIcon ?? MATERIAL_ICONS.Cancel}
              size={badgeIconSizes[size]}
              onClick={handleOnClose}
              {...generateTestId('BadgeCloseIcon')}
            />
          )}
        </BadgeContainer>
      );
    }
  )
)``;
